import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from './supabase';
import { queryKeys } from './queries';
import { serviceKeys } from './serviceQueries';
import type { CrewKind, ServiceSection } from './serviceTypes';

export type { CrewKind, ServiceSection };

// -----------------------------------------------------------------------------
// Phase 10 — Community, Crew and Builder Admin
//
// Eric, 4 September: "We need the capability of managing majority of the system
// and jobs as we move forward and not have to rely on an outside source each
// time... The only time we should need to come back to you are for any changes
// we see needed as we move forward or technical issues."
//
// Four record sets, admin only. Reads come straight off the tables because RLS
// already allows select to any signed in user and the admin page needs the
// inactive rows too, which the dropdown queries in queries.ts deliberately
// filter out with .eq('active', true).
//
// Every WRITE goes through an RPC in 20260916000000_admin_records.sql. Not for
// authorisation, RLS already grants is_admin() ALL on all six tables, but
// because:
//
//   - Deactivating is unguarded at the database level. Nothing stops
//     active = false on a crew with open service items, and every picker in the
//     app filters on active, so the record vanishes while the work stays
//     attached to it. admin_set_record_active refuses that and says how much
//     work and of what.
//   - A raw PostgREST constraint violation reads
//     'duplicate key value violates unique constraint "builders_prefix_key"'.
//     Andrea should read that the prefix is already used by Eastwood Homes.
//   - builders.prefix must be exactly two letters or Andrea's starts sheet
//     import silently matches nothing.
//
// These four tables are small, single and low double digit row counts, so none
// of the paging that flatwork_events needed applies here. Every list is
// explicitly ordered anyway: an unordered select is what let 32 flatwork rows
// go missing on 15 September.
// -----------------------------------------------------------------------------

export interface AdminBuilder {
    id: string;
    name: string;
    prefix: string;
    external_ref: string | null;
    qbo_customer_name: string | null;
    foundation_cost_code: string | null;
    active: boolean;
}

export interface AdminCommunity {
    id: string;
    builder_id: string;
    name: string;
    external_ref: string | null;
    /**
     * The JobTread location the six builder Zaps resolve a PO against. Editable
     * on purpose: it is the only way to correct the seven ghost location ids
     * flagged at the 21 April audit without touching a Zap. A wrong or missing
     * value does not fail loudly, the PO comes back 'Location not configured',
     * so every change here is written to activity_log.
     */
    jt_location_id: string | null;
    latitude: number | null;
    longitude: number | null;
    active: boolean;
    builders: { name: string } | null;
}

export interface AdminCrew {
    id: string;
    name: string;
    kind: CrewKind;
    /** Where Phase 9 sends the day's route. A crew with no address cannot be emailed. */
    email: string | null;
    active: boolean;
}

export interface ServiceCommunityAlias {
    id: string;
    alias: string;
    alias_norm: string;
    community_id: string;
}

export interface AdminServiceCommunity {
    id: string;
    builder_name: string;
    name: string;
    /**
     * Read only. Generated in the database as builder_name || ' - ' || name, so
     * it cannot be written or overridden. Writing to it raises 'cannot insert a
     * non-DEFAULT value into column "display_name"'.
     */
    display_name: string;
    section_id: string | null;
    active: boolean;
    service_sections: { name: string } | null;
    service_community_aliases: ServiceCommunityAlias[];
}

/** Live work hanging off one record. Absent keys mean nothing is attached. */
export interface Dependencies {
    live_jobs?: number;
    live_communities?: number;
    open_stages?: number;
    open_flatwork?: number;
    open_service_items?: number;
    aliases?: number;
}

export interface RecordDependencies {
    builders: Record<string, Dependencies>;
    communities: Record<string, Dependencies>;
    crews: Record<string, Dependencies>;
    service_communities: Record<string, Dependencies>;
}

export type AdminEntity = 'builder' | 'community' | 'crew' | 'service_community';

export const adminKeys = {
    builders: ['admin', 'builders'] as const,
    communities: ['admin', 'communities'] as const,
    crews: ['admin', 'crews'] as const,
    serviceCommunities: ['admin', 'serviceCommunities'] as const,
    serviceSections: ['admin', 'serviceSections'] as const,
    dependencies: ['admin', 'dependencies'] as const,
};

// -----------------------------------------------------------------------------
// Reads. All rows, active and inactive, which is the difference between these
// and the dropdown queries in queries.ts.
// -----------------------------------------------------------------------------

export function useAdminBuilders() {
    return useQuery({
        queryKey: adminKeys.builders,
        queryFn: async (): Promise<AdminBuilder[]> => {
            const { data, error } = await supabase
                .from('builders')
                .select('id, name, prefix, external_ref, qbo_customer_name, foundation_cost_code, active')
                .order('name');
            if (error) throw error;
            return (data ?? []) as AdminBuilder[];
        },
        staleTime: 30_000,
    });
}

export function useAdminCommunities() {
    return useQuery({
        queryKey: adminKeys.communities,
        queryFn: async (): Promise<AdminCommunity[]> => {
            const { data, error } = await supabase
                .from('communities')
                .select(
                    'id, builder_id, name, external_ref, jt_location_id, latitude, longitude, active, builders(name)'
                )
                .order('name');
            if (error) throw error;
            return (data ?? []) as unknown as AdminCommunity[];
        },
        staleTime: 30_000,
    });
}

export function useAdminCrews() {
    return useQuery({
        queryKey: adminKeys.crews,
        queryFn: async (): Promise<AdminCrew[]> => {
            const { data, error } = await supabase
                .from('crews')
                .select('id, name, kind, email, active')
                .order('name');
            if (error) throw error;
            return (data ?? []) as AdminCrew[];
        },
        staleTime: 30_000,
    });
}

export function useAdminServiceCommunities() {
    return useQuery({
        queryKey: adminKeys.serviceCommunities,
        queryFn: async (): Promise<AdminServiceCommunity[]> => {
            const { data, error } = await supabase
                .from('service_communities')
                .select(
                    'id, builder_name, name, display_name, section_id, active, ' +
                        'service_sections(name), ' +
                        'service_community_aliases(id, alias, alias_norm, community_id)'
                )
                .order('builder_name')
                .order('name');
            if (error) throw error;
            return (data ?? []) as unknown as AdminServiceCommunity[];
        },
        staleTime: 30_000,
    });
}

/**
 * Route sections, read only. Managing the sections themselves is out of scope
 * for this milestone and quoted separately, so these are only offered to a
 * service community as a dropdown.
 */
export function useServiceSections() {
    return useQuery({
        queryKey: adminKeys.serviceSections,
        queryFn: async (): Promise<ServiceSection[]> => {
            const { data, error } = await supabase
                .from('service_sections')
                .select('id, name, sort_order, color_key, created_at')
                .order('sort_order');
            if (error) throw error;
            return (data ?? []) as ServiceSection[];
        },
        staleTime: 60_000,
    });
}

/**
 * How much live work hangs off every record, so the count sits beside the
 * switch instead of only appearing when somebody clicks it and gets refused.
 */
export function useRecordDependencies() {
    return useQuery({
        queryKey: adminKeys.dependencies,
        queryFn: async (): Promise<RecordDependencies> => {
            const { data, error } = await supabase.rpc('admin_record_dependencies');
            if (error) throw error;
            return (data ?? {
                builders: {},
                communities: {},
                crews: {},
                service_communities: {},
            }) as RecordDependencies;
        },
        staleTime: 30_000,
    });
}

// -----------------------------------------------------------------------------
// Writes.
//
// Every mutation invalidates its own admin list, the dependency counts, and the
// matching dropdown query in queries.ts. That last one matters: rename a crew
// here and the board's crew picker has to follow, or the office sees two
// different names for the same crew depending which screen they are on.
// -----------------------------------------------------------------------------

function useAdminMutation<TArgs extends object, TResult>(
    fn: string,
    onDone: (qc: ReturnType<typeof useQueryClient>) => void
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args: TArgs): Promise<TResult> => {
            const { data, error } = await supabase.rpc(fn, args);
            // error.message carries the RPC's own sentence, which is written for
            // the office to read. Surface it as is, do not wrap it.
            if (error) throw error;
            return data as TResult;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: adminKeys.dependencies });
            onDone(qc);
        },
    });
}

export interface UpsertBuilderArgs {
    p_id?: string | null;
    p_name: string;
    p_prefix: string;
    p_external_ref?: string | null;
    p_qbo_customer_name?: string | null;
    p_foundation_cost_code?: string | null;
}

export function useUpsertBuilder() {
    return useAdminMutation<UpsertBuilderArgs, AdminBuilder>('admin_upsert_builder', (qc) => {
        qc.invalidateQueries({ queryKey: adminKeys.builders });
        qc.invalidateQueries({ queryKey: queryKeys.builders });
    });
}

export interface UpsertCommunityArgs {
    p_id?: string | null;
    p_builder_id?: string | null;
    p_name: string;
    p_external_ref?: string | null;
    p_jt_location_id?: string | null;
    p_latitude?: number | null;
    p_longitude?: number | null;
}

export function useUpsertCommunity() {
    return useAdminMutation<UpsertCommunityArgs, AdminCommunity>('admin_upsert_community', (qc) => {
        qc.invalidateQueries({ queryKey: adminKeys.communities });
        qc.invalidateQueries({ queryKey: queryKeys.communities });
    });
}

export interface UpsertCrewArgs {
    p_id?: string | null;
    p_name: string;
    p_kind: CrewKind;
    p_email?: string | null;
}

export function useUpsertCrew() {
    return useAdminMutation<UpsertCrewArgs, AdminCrew>('admin_upsert_crew', (qc) => {
        qc.invalidateQueries({ queryKey: adminKeys.crews });
        qc.invalidateQueries({ queryKey: queryKeys.crews });
        // The service board keeps its own crew query, ['serviceCrews', kind].
        // Without this a renamed crew shows one name on the schedule board and
        // the old one on Kristine's, depending which screen you are looking at.
        qc.invalidateQueries({ queryKey: ['serviceCrews'] });
    });
}

export interface UpsertServiceCommunityArgs {
    p_id?: string | null;
    p_builder_name: string;
    p_name: string;
    p_section_id?: string | null;
}

export function useUpsertServiceCommunity() {
    return useAdminMutation<UpsertServiceCommunityArgs, AdminServiceCommunity>(
        'admin_upsert_service_community',
        (qc) => {
            qc.invalidateQueries({ queryKey: adminKeys.serviceCommunities });
            qc.invalidateQueries({ queryKey: serviceKeys.communities });
        }
    );
}

export interface SetRecordActiveArgs {
    p_entity: AdminEntity;
    p_id: string;
    p_active: boolean;
    /**
     * Go ahead even though live work is still attached. Deliberate, and the
     * result records it as forced with the blockers listed. A refusal with no
     * way past it would just mean the office has to ring someone, which is the
     * thing this milestone exists to stop.
     */
    p_force?: boolean;
}

export interface SetRecordActiveResult {
    entity: AdminEntity;
    id: string;
    name: string;
    active: boolean;
    forced: boolean;
    blockers: { what: string; one: string; count: number }[];
}

export function useSetRecordActive() {
    return useAdminMutation<SetRecordActiveArgs, SetRecordActiveResult>(
        'admin_set_record_active',
        (qc) => {
            // Which list changed depends on the entity, and invalidating four
            // small queries costs less than threading the entity through here.
            qc.invalidateQueries({ queryKey: adminKeys.builders });
            qc.invalidateQueries({ queryKey: adminKeys.communities });
            qc.invalidateQueries({ queryKey: adminKeys.crews });
            qc.invalidateQueries({ queryKey: adminKeys.serviceCommunities });
            qc.invalidateQueries({ queryKey: queryKeys.builders });
            qc.invalidateQueries({ queryKey: queryKeys.communities });
            qc.invalidateQueries({ queryKey: queryKeys.crews });
            qc.invalidateQueries({ queryKey: ['serviceCrews'] });
            qc.invalidateQueries({ queryKey: serviceKeys.communities });
        }
    );
}

// -----------------------------------------------------------------------------
// Aliases. Add and remove rather than deactivate, because an alias holds no
// history: it is only what the spreadsheet import matches a typed community
// name against, so a wrong one mismatches future rows and nothing else.
//
// alias_norm is a generated column, lower(regexp_replace(alias,'\s+',' ','g')),
// with a unique index on it, which is what catches the same name typed with
// different spacing or case.
// -----------------------------------------------------------------------------

export function useAddServiceCommunityAlias() {
    return useAdminMutation<{ p_community_id: string; p_alias: string }, ServiceCommunityAlias>(
        'admin_add_service_community_alias',
        (qc) => qc.invalidateQueries({ queryKey: adminKeys.serviceCommunities })
    );
}

export function useDeleteServiceCommunityAlias() {
    return useAdminMutation<{ p_alias_id: string }, ServiceCommunityAlias>(
        'admin_delete_service_community_alias',
        (qc) => qc.invalidateQueries({ queryKey: adminKeys.serviceCommunities })
    );
}

// -----------------------------------------------------------------------------
// Shared helpers for the admin tabs.
// -----------------------------------------------------------------------------

/** Total live work on a record. Zero means it can be switched off unguarded. */
export function dependencyTotal(d: Dependencies | undefined): number {
    if (!d) return 0;
    return (
        (d.live_jobs ?? 0) +
        (d.live_communities ?? 0) +
        (d.open_stages ?? 0) +
        (d.open_flatwork ?? 0) +
        (d.open_service_items ?? 0)
    );
}

/**
 * The same sentence the RPC would refuse with, built client side so the warning
 * can be shown before the click rather than after it. Aliases are excluded on
 * purpose: they do not block anything.
 */
export function dependencySummary(d: Dependencies | undefined): string {
    if (!d) return '';
    const parts: string[] = [];
    const push = (n: number | undefined, one: string, many: string) => {
        if (!n) return;
        parts.push(`${n} ${n === 1 ? one : many}`);
    };
    push(d.live_jobs, 'live job', 'live jobs');
    push(d.live_communities, 'active community', 'active communities');
    push(d.open_stages, 'unfinished stage', 'unfinished stages');
    push(d.open_flatwork, 'unfinished flatwork item', 'unfinished flatwork items');
    push(d.open_service_items, 'open service item', 'open service items');
    return parts.join(' and ');
}

/** RPC errors already read as sentences. Anything else gets a plain fallback. */
export function adminErrorMessage(err: unknown): string {
    if (err && typeof err === 'object' && 'message' in err) {
        const m = String((err as { message: unknown }).message);
        return m.length > 0 ? m : 'That did not save. Try again.';
    }
    return 'That did not save. Try again.';
}
